import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import Icon from 'components/Icon';
import classNames from 'classnames';
import { noop } from 'helpers/GlobalHelper';
import { SegmentUiElements } from '@foxcorp/delta-web-analytics';

/**
 * List Component
 * @param {Object} items       - Items to be displayed
 * @returns {Component}        - React Component
 */
class List extends Component {
  constructor() {
    super();
    this.state = {
      expanded: false,
      expandedItemId: null,
    };
    this.renderListItem = this.renderListItem.bind(this);
    this.toggleItem = this.toggleItem.bind(this);
    this.toggleShowMore = this.toggleShowMore.bind(this);
  }

  renderListItem(item) {
    if (!item) return;
    const { onLinkClick, onTitleClick, onPlayClick, theme } = this.props;
    const { expandedItemId } = this.state;
    const {
      id,
      isLive,
      title,
      titleLink,
      tileDetailToggle,
      link,
      isDisabled,
      icon,
      playButton,
      subtitle,
      description,
      percentWatched,
      badge,
      requiresAuth,
      isAuthenticated,
      isAuthorized,
      target,
      rel,
      originalItem,
      name,
    } = item;

    let titleElement = null;
    if (title) {
      if (!titleLink || isDisabled) {
        titleElement = (
          <div key={`title-div-${id}`} className={theme.title}>
            {title}
          </div>
        );
      } else {
        titleElement = (
          <Link
            key={`title-link-${id}`}
            to={titleLink}
            className={theme.title}
            onClick={() => {
              if (onLinkClick) {
                onLinkClick({
                  originalItem,
                  titleText: name,
                  uiElement: SegmentUiElements.SERIES_TITLE,
                });
              } else if (onTitleClick) {
                onTitleClick();
              }
            }}
            target={target}
            rel={rel}
          >
            <span>{title}</span>
          </Link>
        );
      }
    }

    const playButtonElement =
      playButton === true ? (
        <div className={requiresAuth ? classNames(theme.play, theme.playDimmed) : theme.play}>
          <Link
            key={`link-${id}`}
            to={link}
            onClick={() => {
              if (onPlayClick) {
                onPlayClick({ title, originalItem, uiElement: SegmentUiElements.PLAY });
              } else if (onLinkClick) {
                onLinkClick({ title, originalItem });
              }
            }}
            className={theme.link}
          >
            <Icon icon="play-button" color="white" size="full" />
          </Link>
        </div>
      ) : null;
    let badgeElement = null;
    if (!playButton && badge) {
      const badgeClasses = [theme.badge];
      if (isLive) badgeClasses.push(theme.liveBadge);
      if (badge === 'UPCOMING') badgeClasses.push(theme.otherBadge);
      badgeElement = (
        <div className={classNames(badgeClasses)}>
          <span>{badge}</span>
        </div>
      );
    }
    const subtitleElement = subtitle && (
      <span key="list-subtitle" className={theme.subtitle}>
        {subtitle}
      </span>
    );
    const descriptionElement = description && (
      <span className={theme.description}>{description}</span>
    );
    let percentWatchedUsed = percentWatched;
    if (percentWatchedUsed > 100) percentWatchedUsed = 100;
    let progressBarElement = null;

    if (!Number.isNaN(percentWatchedUsed) && percentWatchedUsed > 0 && percentWatchedUsed <= 100) {
      const progressClassSuffix =
        percentWatchedUsed % 5 === 0
          ? percentWatchedUsed
          : percentWatchedUsed - (percentWatchedUsed % 5);
      progressBarElement = (
        <div className={classNames(theme.progress, `progress-${progressClassSuffix}`)}>
          <div
            className={classNames(theme.done, `done-${progressClassSuffix}`)}
            style={{ width: `${percentWatchedUsed}%` }}
          />
        </div>
      );
    }

    const rightElement = playButtonElement || badgeElement;
    const centerElement = [];

    if (titleElement) centerElement.push(titleElement);
    if (subtitleElement) centerElement.push(subtitleElement);
    if (descriptionElement && expandedItemId === id) centerElement.push(descriptionElement);
    if (progressBarElement) centerElement.push(progressBarElement);

    const dotDotDotLink = (
      <button
        className={theme.dotdotdot}
        onClick={() => {
          this.toggleItem(id);
        }}
      >
        • • •
      </button>
    );
    const xLink = (
      <button
        className={theme.x}
        onClick={() => {
          this.toggleItem(null);
        }}
      >
        <Icon size="full" color="black" icon="close" />
      </button>
    );
    const leftLink = expandedItemId === id ? xLink : dotDotDotLink;

    let leftIcon = null;
    if (icon) leftIcon = icon;

    const leftElement = tileDetailToggle ? leftLink : leftIcon;
    const classes =
      (requiresAuth && !isAuthenticated) || !isAuthorized
        ? classNames(theme.listItem, theme.requiresAuth)
        : theme.listItem;

    // eslint-disable-next-line consistent-return
    return (
      <li key={`li-${id}`} className={classes}>
        {rightElement ? <div className={theme.right}>{rightElement}</div> : null}
        {centerElement ? <div className={theme.center}>{centerElement}</div> : null}
        {leftElement ? <div className={theme.left}>{leftElement}</div> : null}
      </li>
    );
  }

  toggleItem(id) {
    this.setState({ expandedItemId: id });
  }

  toggleShowMore() {
    const { expanded } = this.state;
    this.setState({ expanded: !expanded });
  }

  render() {
    const { expanded } = this.state;
    const {
      items,
      processor,
      theme,
      tileDetailToggle,
      toggleLimit,
      toggleMoreText,
      toggleLessText,
    } = this.props;
    if (!items) return null;
    const key = _.get(items, '0.id', 0);
    const itemsLength = _.get(items, 'length');
    const itemsLimited = toggleLimit && expanded === false ? items.slice(0, toggleLimit) : items;
    const btnText = expanded === false ? toggleMoreText : toggleLessText;

    return (
      <div key={`key-${key}`} className={theme.list}>
        <ul className={theme.listUl}>
          {_.map(itemsLimited, item => {
            const processedItem = processor ? processor({ item }) : item;
            processedItem.tileDetailToggle = tileDetailToggle;
            processedItem.originalItem = item;
            return this.renderListItem(processedItem);
          })}
          {toggleLimit && itemsLength > toggleLimit ? (
            <li className={theme.buttonsContainer}>
              <button onClick={this.toggleShowMore} className={theme.button}>
                {btnText}
              </button>
            </li>
          ) : null}
        </ul>
      </div>
    );
  }
}

List.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})),
  onLinkClick: PropTypes.func,
  onPlayClick: PropTypes.func,
  onTitleClick: PropTypes.func,
  processor: PropTypes.func,
  theme: PropTypes.shape({
    list: PropTypes.string,
    listUl: PropTypes.string,
    listItem: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
  }).isRequired,
  tileDetailToggle: PropTypes.bool,
  toggleLimit: PropTypes.number,
  toggleMoreText: PropTypes.string,
  toggleLessText: PropTypes.string,
};

List.defaultProps = {
  items: [],
  onLinkClick: noop,
  onPlayClick: noop,
  onTitleClick: noop,
  processor: noop,
  tileDetailToggle: false,
  toggleLimit: null,
  toggleMoreText: null,
  toggleLessText: null,
};
export default List;
